import { Code, User, Github, Star, Briefcase, Check, ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useTheme } from "../theme/ThemeContext";
import { useRef, useState } from "react";
import { theme } from "@/theme/theme";

const Services = () => {
  const { theme: themeMode } = useTheme();
  const isDark = themeMode === 'dark';
  const servicesStyles = theme.services[themeMode];
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);

  // Theme transition variants
  const themeTransitionVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  const services = [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Responsive UI Development",
      description: "Creating modern, responsive user interfaces that work seamlessly across all devices and browsers.",
      color: isDark ? "from-orange-500/20 to-orange-600/20" : "from-teal-500/20 to-teal-600/20"
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Full-Stack Web Applications",
      description: "End-to-end development of web applications with robust frontend and backend architectures.",
      color: isDark ? "from-orange-400/20 to-orange-500/20" : "from-teal-400/20 to-teal-500/20"
    },
    {
      icon: <Github className="w-8 h-8" />,
      title: "REST API Integration",
      description: "Seamless integration of RESTful APIs and third-party services for enhanced functionality.",
      color: isDark ? "from-orange-300/20 to-orange-400/20" : "from-teal-300/20 to-teal-400/20"
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Admin Dashboards",
      description: "Comprehensive admin panels and dashboards for data management and business operations.",
      color: isDark ? "from-orange-600/20 to-orange-700/20" : "from-teal-600/20 to-teal-700/20"
    },
    {
      icon: <Check className="w-8 h-8" />,
      title: "Real-time Features",
      description: "Implementation of real-time functionality using WebSockets and modern technologies.",
      color: isDark ? "from-orange-700/20 to-orange-800/20" : "from-teal-700/20 to-teal-800/20"
    },
    {
      icon: <User className="w-8 h-8" />,
      title: "Technical Mentorship",
      description: "Guidance and mentorship for developers looking to improve their skills and career growth.",
      color: isDark ? "from-orange-800/20 to-orange-900/20" : "from-teal-800/20 to-teal-900/20"
    }
  ];

  return (
    <section
      ref={containerRef}
      id="services"
      className={`min-h-screen py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden transition-colors duration-500 ${servicesStyles.background}`}
    >
      {/* Animated Background with Theme Transition */}
      <AnimatePresence mode="wait">
        <motion.div
          key={themeMode}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={themeTransitionVariants}
          className="absolute inset-0 pointer-events-none"
        >
          <motion.div
            style={{ y, opacity }}
            className="absolute inset-0"
          >
            <div className={`absolute inset-0 bg-gradient-to-br transition-colors duration-500 ${servicesStyles.gradient.primary} to-transparent`} />
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className={`absolute top-1/4 left-1/4 w-96 h-96 transition-colors duration-500 ${servicesStyles.gradient.secondary} rounded-full blur-3xl`}
            />
            <motion.div
              animate={{
                scale: [1.2, 1, 1.2],
                rotate: [5, 0, 5],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "linear"
              }}
              className={`absolute bottom-1/4 right-1/4 w-96 h-96 transition-colors duration-500 ${servicesStyles.gradient.secondary} rounded-full blur-3xl`}
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <div className="max-w-7xl mx-auto relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={themeMode}
            initial={{ opacity: 0, y: -24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            className="text-center mb-8 sm:mb-12 md:mb-16"
          >
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-500 font-mono ${servicesStyles.text.heading}`}>
              What I Offer
            </h2>
            <motion.div
              className={`w-16 sm:w-20 h-1 mx-auto rounded-full ${isDark ? 'bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400' : 'bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400'} transition-colors duration-500`}
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.7, ease: 'circOut' }}
              style={{ originX: 0.5 }}
            />
            <p className={`transition-colors duration-500 ${servicesStyles.text.description} text-lg md:text-xl max-w-3xl mx-auto leading-relaxed font-mono mt-6`}>
              Specialized in frontend development with strong full-stack capabilities,
              delivering comprehensive web solutions for modern businesses.
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Mobile View with Theme Transition */}
        <div className="md:hidden">
          <div className="space-y-6">
            {services.map((service, index) => (
              <AnimatePresence mode="wait" key={index}>
                <motion.div
                  key={themeMode}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={themeTransitionVariants}
                  className="relative"
                >
                  <motion.div
                    onHoverStart={() => setActiveIndex(index)}
                    onHoverEnd={() => setActiveIndex(null)}
                    className={`relative overflow-hidden rounded-2xl transition-colors duration-500 ${servicesStyles.card.background} backdrop-blur-sm`}
                  >
                    {/* Service Content */}
                    <div className="relative z-10 p-6">
                      <div className="flex items-start gap-4">
                        <motion.div
                          animate={{
                            scale: activeIndex === index ? 1.2 : 1,
                            rotate: activeIndex === index ? 360 : 0,
                          }}
                          transition={{ duration: 0.5 }}
                          className={`flex-shrink-0 w-12 h-12 rounded-xl transition-colors duration-500 ${servicesStyles.icon.background} flex items-center justify-center ${servicesStyles.icon.color}`}
                        >
                          {service.icon}
                        </motion.div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold mb-2 transition-colors duration-500 ${servicesStyles.text.main} font-mono`}>
                            {service.title}
                          </h3>
                          <p className={`transition-colors duration-500 ${servicesStyles.text.description} leading-relaxed text-sm font-mono`}>
                            {service.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Animated Background */}
                    <motion.div
                      animate={{
                        scale: activeIndex === index ? 1.1 : 1,
                        opacity: activeIndex === index ? 1 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                      className={`absolute inset-0 transition-colors duration-500 bg-gradient-to-br ${service.color}`}
                    />

                    {/* Interactive Border */}
                    <motion.div
                      animate={{
                        scale: activeIndex === index ? 1.02 : 1,
                      }}
                      transition={{ duration: 0.3 }}
                      className={`absolute inset-0 rounded-2xl border-2 transition-colors duration-500 ${servicesStyles.card.border}`}
                    />
                  </motion.div>

                  {/* Connecting Line */}
                  {index < services.length - 1 && (
                    <div className={`absolute left-6 top-full w-0.5 h-6 transition-colors duration-500 ${servicesStyles.line.background}`} />
                  )}
                </motion.div>
              </AnimatePresence>
            ))}
          </div>
        </div>

        {/* Desktop View with Theme Transition */}
        <div className="hidden md:block relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={themeMode}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={themeTransitionVariants}
              className={`absolute left-1/2 top-0 bottom-0 w-0.5 transition-colors duration-500 ${isDark ? 'bg-gradient-to-b from-orange-500/50 via-orange-500 to-orange-500/50' : 'bg-gradient-to-b from-teal-500/50 via-teal-500 to-teal-500/50'}`}
            />
          </AnimatePresence>

          <div className="space-y-32">
            {services.map((service, index) => (
              <AnimatePresence mode="wait" key={index}>
                <motion.div
                  key={themeMode}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={themeTransitionVariants}
                  className={`relative flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-16' : 'pl-16'}`}>
                    <motion.div
                      onHoverStart={() => setActiveIndex(index)}
                      onHoverEnd={() => setActiveIndex(null)}
                      className={`relative group ${index % 2 === 0 ? 'text-right' : 'text-left'}`}
                    >
                      <motion.div
                        animate={{
                          scale: activeIndex === index ? 1.2 : 1,
                          rotate: activeIndex === index ? 360 : 0,
                        }}
                        transition={{ duration: 0.5 }}
                        className={`absolute ${index % 2 === 0 ? 'right-[-3rem]' : 'left-[-3rem]'} top-1/2 -translate-y-1/2 w-12 h-12 rounded-full ${servicesStyles.icon.background} flex items-center justify-center ${servicesStyles.icon.color}`}
                      >
                        {service.icon}
                      </motion.div>

                      <motion.div
                        animate={{
                          x: activeIndex === index ? (index % 2 === 0 ? -20 : 20) : 0,
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      >
                        <h3 className={`text-2xl font-semibold mb-4 ${servicesStyles.text.main} font-mono`}>
                          {service.title}
                        </h3>
                        <p className={`${servicesStyles.text.description} leading-relaxed font-mono`}>
                          {service.description}
                        </p>
                      </motion.div>
                    </motion.div>
                  </div>

                  <div className="w-1/2" />
                </motion.div>
              </AnimatePresence>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
